/** @jsxImportSource @emotion/react */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import MuiButton from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Button from '@Button';
import Text from '@Text';
import ClientApiJourney from './ClientApiJourney';
import {
  selectChameleonStrategyCollection,
  selectJourneyNotOfferedReason,
  hasUnknownComponentStep,
} from '../../Helpers/strategyCollection.helpers';
import makeClientApiStyles from './makeClientApiStyles';
import { exposeUserActionEvent } from '../../Helpers/events';
import { updateRecalculateRequiredWidgetHeight } from '../../Lib/redux/slices/dynamicHeight';
import { setShouldNavigateForward } from '../../Lib/redux/slices/navigationControls';
import { refuseClientApiJourneyAction } from '../../Hooks/actionCreators';
import { useLeadStatusPoll } from '../../Hooks';
import { incrementMetric } from '../../Services/Datadog';
import FetchStatus from '../../Data/FetchStatus';

const ClientApi = ({ componentId, iFrameId }) => {
  const dispatch = useDispatch();
  const reduxStore = useStore();
  const theme = useTheme();
  const classes = makeClientApiStyles({ theme });

  const isDynamicHeightEnabled = useSelector(
    (store) => store.optionalFormConfig?.isDynamicHeightEnabled
  );
  const component = useSelector(
    (i) => i.components[`component_${componentId}`]
  );
  const {
    prompt_enabled: promptEnabled,
    prompt_heading: promptHeading,
    prompt_subheading: promptSubheading,
    prompt_yes_cta: promptYesCta,
    prompt_no_cta: promptNoCta,
    prompt_footnote: promptFootnote,
  } = component.component.additionalSettings;
  const hasPromptFootnote = Boolean(promptFootnote?.trim());

  // The journey runs when the form's method is Chameleon and the top matched
  // subscription declares one for us; each step reads that same collection.
  const isJourneyOnOffer = useSelector((store) =>
    Boolean(selectChameleonStrategyCollection(store))
  );

  // When the prompt is disabled we skip straight into the booking journey.
  const [hasStartedBooking, setHasStartedBooking] = useState(!promptEnabled);

  const bookingStatus = useSelector(
    (store) => store.clientApiJourney.appointment?.bookingStatus
  );

  useLeadStatusPoll({
    shouldPoll:
      isJourneyOnOffer &&
      hasStartedBooking &&
      bookingStatus !== FetchStatus.COMPLETED,
  });

  useEffect(() => {
    if (!isJourneyOnOffer) {
      // A form carrying this component and no journey to run is worth seeing:
      // falling through to an ordinary lead looks the same as matching nothing.
      const state = reduxStore.getState();
      const reason = selectJourneyNotOfferedReason(state);

      if (reason) {
        incrementMetric({
          name: 'clientApiJourneyNotOffered',
          tags: { reason, form_id: state.formConfig?.formId },
        });
      }

      dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
      return;
    }

    // The journey is offered, but a step may have degraded to the type default
    // or been skipped because its component is not in this build. Count that so
    // a replaced or missing screen is never silent.
    const state = reduxStore.getState();
    if (hasUnknownComponentStep(selectChameleonStrategyCollection(state))) {
      incrementMetric({
        name: 'clientApiIntendedScreenNotShown',
        tags: { form_id: state.formConfig?.formId },
      });
    }

    exposeUserActionEvent({
      type: promptEnabled ? 'bookingPromptShown' : 'bookingStarted',
      iFrameId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isJourneyOnOffer]);

  // Moving from the prompt into the store list is an internal view change, not
  // a form page navigation, so the widget sizing must be refreshed here.
  useEffect(() => {
    if (!hasStartedBooking) return;
    if (isDynamicHeightEnabled) {
      // Dynamic height: recalculate the widget height so the iframe resizes.
      dispatch(
        updateRecalculateRequiredWidgetHeight({
          isWidgetHeightRecalculationRequired: true,
        })
      );
    } else {
      // Static height: the ScrollContainer only re-measures its y-overflow on
      // page transitions/window resize, so nudge it to re-evaluate now that the
      // store list has replaced the prompt.
      window.dispatchEvent(new Event('resize'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasStartedBooking]);

  const handleYesClick = () => {
    exposeUserActionEvent({ type: 'bookingStarted', iFrameId });
    incrementMetric({ name: 'clientApiBookingJourneyStarted' });
    setHasStartedBooking(true);
  };

  const handleNoClick = () => {
    exposeUserActionEvent({ type: 'bookingDeclined', iFrameId });
    dispatch(refuseClientApiJourneyAction());
    dispatch(setShouldNavigateForward({ shouldNavigateForward: true }));
  };

  if (!isJourneyOnOffer) return null;

  if (hasStartedBooking) {
    return <ClientApiJourney componentId={componentId} iFrameId={iFrameId} />;
  }

  return (
    <div data-tf-element-role="offer">
      <Grid container direction="column">
        <Container css={classes.promptTextContainer}>
          <Box css={classes.heading}>
            <Text variant="question">{promptHeading}</Text>
          </Box>
          <Box css={classes.subheading}>
            <Text variant="text">{promptSubheading}</Text>
          </Box>
        </Container>
        <Container maxWidth="md" css={classes.promptFooterContainer}>
          <Box
            css={classes.yesButtonContainer}
            data-testid="bookingYesContainer"
          >
            <Button
              label={promptYesCta}
              onClick={handleYesClick}
              dataTestId="bookingYesCta"
            />
          </Box>
          <Box data-testid="bookingNoContainer">
            <MuiButton
              css={classes.noButton}
              onClick={handleNoClick}
              data-testid="bookingNoCta"
            >
              {promptNoCta}
            </MuiButton>
            {hasPromptFootnote && (
              <Box css={classes.footnote}>
                <Text variant="footer" dataTestId="bookingPromptFootnote">
                  {promptFootnote}
                </Text>
              </Box>
            )}
          </Box>
        </Container>
      </Grid>
    </div>
  );
};

export default ClientApi;
